import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';

const PILL_BG = 'rgba(248, 246, 253, 0.6)';
const PILL_BORDER = 'rgba(34, 28, 90, 0.10)';
const PILL_TEXT = '#5A5578';
const PILL_ACTIVE_BG_MIX = '0D';
const PILL_ACTIVE_BORDER_MIX = '4D';

export const TriggerButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'bordered' && prop !== 'pill' && prop !== 'active',
})<{ bordered?: boolean; pill?: boolean; active?: boolean }>(({ theme, bordered, pill, active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: 40,
  minWidth: pill ? 82 : 140,
  width: bordered ? '100%' : undefined,
  padding: pill ? theme.spacing(0, 1.75) : theme.spacing(0, 1.5),
  fontSize: pill ? '0.75rem' : '0.875rem',
  fontWeight: pill ? 500 : 400,
  fontFamily: 'inherit',
  color: pill && active ? theme.palette.primary.main : pill ? PILL_TEXT : theme.palette.text.primary,
  backgroundColor:
    pill && active
      ? `${theme.palette.primary.main}${PILL_ACTIVE_BG_MIX}`
      : pill
        ? PILL_BG
        : theme.palette.background.paper,
  border:
    pill && active
      ? `1px solid ${theme.palette.primary.main}${PILL_ACTIVE_BORDER_MIX}`
      : pill
        ? `1px solid ${PILL_BORDER}`
        : bordered
          ? `1px solid ${theme.palette.divider}`
          : 'none',
  borderRadius: pill ? 10 : theme.shape.borderRadius,
  cursor: 'pointer',
  gap: pill ? theme.spacing(0.75) : theme.spacing(0.5),
  transition: theme.transitions.create(['border-color', 'background-color'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    backgroundColor:
      pill && active
        ? `${theme.palette.primary.main}${PILL_ACTIVE_BG_MIX}`
        : pill
          ? PILL_BG
          : theme.palette.action.hover,
    borderColor:
      pill && active
        ? `${theme.palette.primary.main}${PILL_ACTIVE_BORDER_MIX}`
        : pill
          ? PILL_BORDER
          : bordered
            ? theme.palette.text.secondary
            : undefined,
  },
  '&:focus-visible': bordered
    ? { outline: 'none', borderColor: theme.palette.primary.main, borderWidth: 2 }
    : {},
}));

export const CountBadge = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 999,
  backgroundColor: `${theme.palette.primary.main}26`,
  color: theme.palette.primary.main,
  fontSize: '0.625rem',
  fontWeight: 700,
  padding: theme.spacing(0, 0.5),
  lineHeight: '14px',
  height: 14,
}));

export const DropdownPaper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'pill',
})<{ pill?: boolean }>(({ theme, pill }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: 240,
  maxHeight: pill ? undefined : 320,
  padding: pill ? theme.spacing(1) : theme.spacing(1, 0, 0.5),
}));

export const SearchWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'pill',
})<{ pill?: boolean }>(({ theme, pill }) => ({
  padding: pill ? theme.spacing(0, 0, 1) : theme.spacing(0, 1, 1),
  flexShrink: 0,
}));

export const OptionsList = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'pill',
})<{ pill?: boolean }>(({ theme, pill }) => ({
  overflowY: 'auto',
  flex: 1,
  maxHeight: pill ? 208 : undefined,
  ...(pill ? { display: 'flex', flexDirection: 'column', gap: 2 } : null),
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: theme.palette.divider, borderRadius: 3 },
  '&::-webkit-scrollbar-thumb:hover': { background: theme.palette.text.disabled },
}));

export const OptionRow = styled('label', {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'pill',
})<{ isSelected?: boolean; pill?: boolean }>(({ theme, isSelected, pill }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: pill ? theme.spacing(1) : theme.spacing(0.5),
  padding: pill ? theme.spacing(0.75, 1) : theme.spacing(0.5, 1.5, 0.5, 0.75),
  borderRadius: pill ? 6 : 0,
  cursor: 'pointer',
  fontSize: pill ? '0.75rem' : '0.875rem',
  userSelect: 'none',
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  ...(pill
    ? {
        color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
        backgroundColor: isSelected ? `${theme.palette.primary.main}1A` : 'transparent',
        fontWeight: isSelected ? 500 : 400,
        '&:hover': {
          backgroundColor: isSelected
            ? `${theme.palette.primary.main}26`
            : theme.palette.action.hover,
        },
      }
    : {
        color: isSelected ? theme.palette.secondary.dark : theme.palette.text.primary,
        backgroundColor: isSelected ? `${theme.palette.secondary.main}28` : 'transparent',
        fontWeight: isSelected ? 600 : 400,
        '&:hover': {
          backgroundColor: isSelected
            ? `${theme.palette.secondary.main}38`
            : theme.palette.action.hover,
        },
      }),
}));

export const MiniCheckbox = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 14,
  height: 14,
  borderRadius: 4,
  border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: isSelected ? theme.palette.primary.main : 'transparent',
  color: '#fff',
  fontSize: 9,
  fontWeight: 700,
  lineHeight: 1,
  transition: theme.transitions.create(['background-color', 'border-color'], {
    duration: theme.transitions.duration.shortest,
  }),
}));

export const PillChevron = styled(KeyboardArrowDownIcon)({
  fontSize: 12,
  flexShrink: 0,
  opacity: 0.6,
});

export const DefaultChevron = styled(ArrowDropDownIcon)({
  fontSize: 20,
  flexShrink: 0,
});

export const HiddenCheckbox = styled('input')({
  position: 'absolute',
  opacity: 0,
  pointerEvents: 'none',
});

export const TruncatedText = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const EmptyText = styled('span')(({ theme }) => ({
  fontSize: '0.8rem',
  color: theme.palette.text.disabled,
  padding: theme.spacing(1.5, 2),
  display: 'block',
  textAlign: 'center',
}));

export const ShowSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 1, 0.5),
  flexShrink: 0,
}));

export const ShowHeader = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  background: 'none',
  border: 'none',
  padding: theme.spacing(0.5, 0.5),
  cursor: 'pointer',
  fontFamily: 'inherit',
  borderRadius: theme.shape.borderRadius,
  '&:hover': { backgroundColor: theme.palette.action.hover },
}));

export const ShowLabel = styled('span')(({ theme }) => ({
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
}));

export const ShowOption = styled('button')<{ selected?: boolean }>(({ theme, selected }) => ({
  display: 'block',
  width: '100%',
  textAlign: 'left',
  background: selected ? theme.palette.secondary.main + '18' : 'none',
  border: 'none',
  padding: theme.spacing(0.5, 1),
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.875rem',
  color: selected ? theme.palette.secondary.dark : theme.palette.text.primary,
  borderRadius: theme.shape.borderRadius,
  '&:hover': { backgroundColor: theme.palette.secondary.main + '0F' },
}));

export const SelectAllRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'pill',
})<{ pill?: boolean }>(({ theme, pill }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: pill ? theme.spacing(0.5, 1, 0.75) : theme.spacing(0.5, 1.5, 0.5, 0.75),
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: pill ? theme.spacing(0.5) : 0,
}));

export const SelectAllLabel = styled('label', {
  shouldForwardProp: (prop) => prop !== 'pill',
})<{ pill?: boolean }>(({ theme, pill }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: pill ? theme.spacing(1) : theme.spacing(0.5),
  cursor: 'pointer',
  fontSize: pill ? '0.75rem' : '0.8rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  userSelect: 'none' as const,
}));

export const InlineClearAllButton = styled('button')(({ theme }) => ({
  marginLeft: 'auto',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: theme.palette.text.disabled,
  '&:hover': { color: theme.palette.text.primary },
}));

export const ClearAllButton = styled('button')(({ theme }) => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  marginBottom: theme.spacing(0.75),
  padding: theme.spacing(0.75, 1),
  borderRadius: 6,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.6875rem',
  fontWeight: 500,
  color: theme.palette.text.disabled,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
}));

export const searchInputSx = {
  fontSize: '0.82rem',
  height: 34,
  borderRadius: 1,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: (theme: any) => theme.palette.divider,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: (theme: any) => theme.palette.text.disabled,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: (theme: any) => theme.palette.divider,
    borderWidth: 2,
  },
  '& .MuiInputAdornment-root': {
    color: (theme: any) => theme.palette.text.disabled,
  },
  '&.Mui-focused .MuiInputAdornment-root': {
    color: (theme: any) => theme.palette.divider,
  },
} as const;

export const checkboxSx = {
  p: 0.5,
  color: 'text.disabled',
  '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: 'primary.main' },
} as const;

export const checkboxOptionSx = {
  p: 0.5,
  color: 'text.disabled',
  '&.Mui-checked': { color: 'primary.main' },
} as const;

export const showHeaderInnerSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  ml: 'auto',
} as const;

export const popoverSx = { zIndex: 1400 } as const;

export const showFilterLabelStyle = { fontSize: '0.8rem', color: 'inherit' } as const;

export const showOptionsBoxSx = { pt: 0.25 } as const;
